import React, { useState, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import * as nunjucks from 'nunjucks';

const useStyles = makeStyles(theme => ({
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
  },
  dragOver: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(0.5),
  },
  hiddenInput: {
    display: 'none',
  },
  errorBox: {
    padding: theme.spacing(1, 2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

export const FileUpload = (props: FieldExtensionComponentProps<any>) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    uiSchema,
    formContext,
  } = props;

  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<{fileName: string, fileSize: number} | null>(null);

  console.log('SimpleFileUpload: Current formData:', formData);
  console.log('SimpleFileUpload: formContext:', formContext);

  // Helper function to resolve Backstage template syntax in UI options
  const resolveTemplate = (template: string, context: any): string => {
    if (!template || typeof template !== 'string') {
      return template;
    }
    
    try {
      // Convert Backstage template syntax ${{ variable }} to Nunjucks syntax {{ variable }}
      let nunjucksTemplate = template;
      
      // Replace ${{ parameters.variableName }} with {{ parameters.variableName }}
      nunjucksTemplate = nunjucksTemplate.replace(/\$\{\{\s*([^}]+)\s*\}\}/g, '{{ $1 }}');
      
      console.log(`Converting Backstage template "${template}" to Nunjucks template "${nunjucksTemplate}"`);
      
      // Configure nunjucks to not auto-escape (since we're dealing with paths/filenames)
      const env = new nunjucks.Environment();
      env.addFilter('dump', (obj) => JSON.stringify(obj, null, 2));
      
      // Create template context with parameters namespace
      // Also include direct access to variables for simpler templates
      const templateContext = {
        parameters: context || {},
        ...context // Allow direct access like {{ adoProject }} instead of {{ parameters.adoProject }}
      };
      
      // Use formContext data for template resolution
      const resolvedValue = env.renderString(nunjucksTemplate, templateContext);
      console.log(`Resolved template to "${resolvedValue}"`);
      return resolvedValue;
    } catch (error) {
      console.warn(`Failed to resolve template "${template}":`, error);
      return template; // Return original if template resolution fails
    }
  };

  // Get form context data for template resolution
  // Try multiple possible locations for form data
  const allFormData = formContext?.formData || props.formContext?.formData || {};
  
  // Extract individual parameters from the form data structure
  const templateContext = {};
  
  // Look for parameters in various possible structures
  if (allFormData) {
    // Direct access to form fields
    Object.keys(allFormData).forEach(key => {
      if (typeof allFormData[key] === 'string' || typeof allFormData[key] === 'number') {
        templateContext[key] = allFormData[key];
      }
    });
  }
  
  console.log('All form data:', allFormData);
  console.log('Extracted template context:', templateContext);

  const maxFileSize = uiSchema['ui:options']?.maxFileSize || uiSchema['ui:maxFileSize'] || 100 * 1024 * 1024; // 100MB default (S3 direct upload supports larger files)
  const acceptedFileTypes = uiSchema['ui:options']?.acceptedFileTypes || uiSchema['ui:acceptedFileTypes'] || '.zip,.tar.gz,.tar';
  
  // Resolve templates in UI options
  const rawS3KeyPrefix = uiSchema['ui:options']?.s3KeyPrefix || uiSchema['ui:s3KeyPrefix'] || 'uploads';
  const rawCustomFilename = uiSchema['ui:options']?.filename || uiSchema['ui:filename'];
  
  let s3KeyPrefix = resolveTemplate(rawS3KeyPrefix, templateContext);
  let customFilename = resolveTemplate(rawCustomFilename, templateContext);
  
  // If template resolution results in empty/undefined values, fall back to defaults
  if (!s3KeyPrefix || s3KeyPrefix.trim() === '' || s3KeyPrefix.includes('{{') || s3KeyPrefix.includes('}}')) {
    console.warn('Template resolution failed or incomplete for s3KeyPrefix, using fallback');
    s3KeyPrefix = 'uploads'; // Fallback
  }
  
  if (customFilename && (customFilename.includes('{{') || customFilename.includes('}}'))) {
    console.warn('Template resolution failed or incomplete for customFilename, clearing value');
    customFilename = undefined; // Clear if template resolution failed
  }
  
  console.log('UI Schema options:', uiSchema['ui:options']);
  console.log('Template context:', templateContext);
  console.log('Raw S3 key prefix:', rawS3KeyPrefix, '-> Resolved:', s3KeyPrefix);
  console.log('Raw custom filename:', rawCustomFilename, '-> Resolved:', customFilename);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds maximum allowed size (${(maxFileSize / 1024 / 1024).toFixed(2)} MB)`;
    }

    // Note: With S3 direct upload, server payload limits are not an issue for the file upload itself
    // Still validate against reasonable limits to prevent excessive processing on the backend
    const maxProcessingSize = 500 * 1024 * 1024; // 500MB processing limit
    if (file.size > maxProcessingSize) {
      return `File too large for processing. Maximum supported size: ${(maxProcessingSize / 1024 / 1024).toFixed(0)} MB. Current file: ${(file.size / 1024 / 1024).toFixed(2)} MB.`;
    }

    // Check file type
    const allowedTypes = acceptedFileTypes.split(',').map((type: string) => type.trim());
    
    // Handle compound extensions like .tar.gz, .tar.bz2
    let fileExtension = '';
    const fileName = file.name.toLowerCase();
    
    // Check for compound extensions first
    if (fileName.endsWith('.tar.gz')) {
      fileExtension = '.tar.gz';
    } else if (fileName.endsWith('.tar.bz2')) {
      fileExtension = '.tar.bz2';
    } else {
      // For simple extensions, get the last part
      fileExtension = `.${file.name.split('.').pop()}`;
    }
    
    if (!allowedTypes.includes(fileExtension)) {
      return `File type "${fileExtension}" is not allowed. Accepted types: ${acceptedFileTypes}`;
    }

    return null;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadToS3 = async (file: File): Promise<any> => {
    console.log(`Starting S3 upload for ${file.name}`);
    console.log(`File size: ${file.size} bytes, Type: ${file.type}`);

    try {
      // Step 1: Get the scaffolder backend URL using Discovery API
      setUploadError('Preparing upload...');
      const scaffolderBaseUrl = await discoveryApi.getBaseUrl('scaffolder');
      console.log(`Using scaffolder backend URL: ${scaffolderBaseUrl}`);
      
      // Get authentication token
      const { token } = await identityApi.getCredentials();
      console.log('Got authentication token for API request');
      
      // Step 2: Get presigned URL from backend
      const presignedResponse = await fetch(`${scaffolderBaseUrl}/api/s3/presigned-upload`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream',
          s3KeyPrefix: s3KeyPrefix,
          customFilename: customFilename,
        }),
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json().catch(() => ({}));
        throw new Error(`Failed to get presigned URL: ${presignedResponse.status} ${errorData.error || ''}`);
      }

      const { uploadUrl, s3Key } = await presignedResponse.json();
      console.log(`Got presigned URL for S3 key: ${s3Key}`);

      // Step 2: Upload directly to S3
      setUploadError('Uploading to S3...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log(`Successfully uploaded to S3: ${s3Key}`);

      // Return S3 information instead of file content
      const bucketName = 'backstage-artifact-store'; // Fixed bucket name
      const finalFileName = customFilename || file.name; // Use custom filename if provided
      const s3Info = {
        s3Key: s3Key || '',
        bucket: bucketName,
        fileName: finalFileName || '',
        originalFileName: file.name || '', // Keep track of original filename too
        fileSize: file.size || 0,
        uploadedAt: new Date().toISOString(),
        s3Url: `s3://${bucketName}/${s3Key}`,
      };

      console.log(`Upload successful, returning S3 info:`, s3Info);
      
      // Validate that all required properties are present
      if (!s3Info.s3Key || !s3Info.fileName) {
        throw new Error('Missing required S3 information');
      }
      
      // Return the object directly instead of JSON string
      return s3Info;

    } catch (error) {
      console.error('S3 upload failed:', error);
      throw error;
    }
  };

         const handleFileChange = async (file: File) => {
           console.log('Processing file:', file.name, 'Size:', file.size);
           
           const validationError = validateFile(file);
           if (validationError) {
             setUploadError(validationError);
             return;
           }

           setUploadError(null);
           setFileInfo({
             fileName: file.name,
             fileSize: file.size
           });

           try {
             // Use S3 upload for all files
             console.log('Using S3 upload for file:', file.name);
             const s3InfoObject = await uploadToS3(file);
             
             console.log('Setting S3 info object:', s3InfoObject);
             setUploadError(null); // Clear progress messages
             onChange(s3InfoObject);
             
           } catch (error) {
             setUploadError(`Upload failed: ${error}`);
             onChange(undefined);
           }
         };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = async () => {
    console.log('SimpleFileUpload: Clearing file');
    
    // If we have an S3 object, try to delete it first
    if (formData && typeof formData === 'object' && formData.s3Key) {
      try {
        setUploadError('Deleting file...');
        const scaffolderBaseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        
        console.log(`Attempting to delete S3 object: ${formData.s3Key}`);
        
        const deleteResponse = await fetch(`${scaffolderBaseUrl}/api/s3/delete/${encodeURIComponent(formData.s3Key)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (deleteResponse.ok) {
          const result = await deleteResponse.json();
          console.log('S3 object deleted successfully:', result);
        } else {
          console.warn('Failed to delete S3 object:', deleteResponse.status, deleteResponse.statusText);
          // Continue with UI cleanup even if deletion fails
        }
        
      } catch (error) {
        console.warn('Error deleting S3 object:', error);
        // Continue with UI cleanup even if deletion fails
      }
    }
    
    // Clear UI state
    onChange(undefined);
    setFileInfo(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileChange(event.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileChange(event.target.files[0]);
    }
  };

  const hasFile = formData && (
    (typeof formData === 'string' && formData.length > 0) ||
    (typeof formData === 'object' && formData.s3Key)
  );

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {schema.title}
        {required && <span style={{ color: 'red' }}> *</span>}
      </Typography>
      
      {schema.description && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {schema.description}
        </Typography>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        className={classes.hiddenInput}
        accept={acceptedFileTypes}
      />
      
      {!hasFile ? (
        <Box
          className={`${classes.dropZone} ${dragOver ? classes.dragOver : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <CloudUploadIcon fontSize="large" color="action" />
          <Typography variant="body2" color="textSecondary">
            Drag & drop your zip file here, or click to browse
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadIcon />}
            style={{ marginTop: 16 }}
            onClick={(e) => {
              e.stopPropagation();
              handleBrowseClick();
            }}
          >
            Browse Files
          </Button>
        </Box>
      ) : (
        <Box className={classes.fileInfo}>
          <Box>
            <Typography variant="body1">
              {fileInfo?.fileName || (typeof formData === 'object' ? formData.fileName : 'File uploaded')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {fileInfo ? 
                formatFileSize(fileInfo.fileSize) : 
                (typeof formData === 'object' && formData.fileSize ? 
                  formatFileSize(formData.fileSize) : 
                  'S3 Upload'
                )
              }
            </Typography>
            {typeof formData === 'object' && formData.s3Key && (
              <Typography variant="caption" color="textSecondary">
                S3: {formData.s3Key}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClearFile} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      {uploadError && (
        <Paper className={classes.errorBox}>
          <Typography variant="body2">
            {uploadError}
          </Typography>
        </Paper>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <Paper className={classes.errorBox}>
          <Typography variant="body2">
            {rawErrors[0]}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};